import { useState } from "react"
import {
	IconDotsOutline18 as Dots,
	IconHashtagOutline18 as Hashtag,
} from "nucleo-ui-outline-18"

import type { SlackConnectSettings } from "@productlane/zero"
import type { UpdateSlackConnectSettings } from "@productlane/zero/types"
import { cn } from "@productlane/lib/src/cn"
import {
	AlertDialog,
	Avatar,
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	Select,
	Switch,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { RouterOutputs } from "@/lib/api"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useZero } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"
import {
	SETTINGS_CARD_NOTICE_CLASS,
	SettingsContainer,
} from "../settings-switches/settings-container"
import {
	channelSummary,
	NEW_CHANNEL_OPTIONS,
	slackDomain,
} from "./slack-channel-list"
import { SlackChannelSelect } from "./slack-channel-select"
import {
	GROUPING_OPTIONS,
	groupingValue,
	parseGroupingValue,
} from "./slack-grouping"
import { LINEAR_ASKS_OPTIONS, linearAsksModeValue } from "./slack-linear-asks"
import { SlackWelcomeMessages } from "./slack-welcome-messages"

type SlackConnectionStatus =
	RouterOutputs["workspace"]["slackSettings"]["connections"][number]

export const SLACK_MISSING_SCOPES_NOTICE =
	"Reconnect Slack to turn on the latest Slack features."

/** A token from before a scope was added keeps its old grant until the workspace reconnects. */
export function needsScopeReconnect(
	status: Pick<SlackConnectionStatus, "error" | "missingScopes"> | undefined,
): boolean {
	return !status?.error && (status?.missingScopes?.length ?? 0) > 0
}

export function connectSlackWorkspace() {
	analytics.track("connect-slack")
	window.location.href = `${env.VITE_API_URL}/api/auth/slack?state=slack-connect`
}

/** One connected Slack workspace: its settings, welcome messages and a link to its channels. */
export function SlackWorkspaceCard({
	settings,
	status,
	paused,
}: {
	settings: SlackConnectSettings
	status: SlackConnectionStatus | undefined
	paused: boolean
}) {
	const zero = useZero()
	const utils = api.useUtils()
	const [disconnectOpen, setDisconnectOpen] = useState(false)
	const disconnect = api.workspace.disconnectSlack.useMutation({
		onSuccess: () => utils.workspace.slackSettings.invalidate(),
		onError: (error) =>
			toast.error({
				title: "Slack is still connected",
				description: error.message,
			}),
	})

	const domain = slackDomain(settings.teamUrl)
	const name = status?.slackTeam?.name ?? domain

	const update = async (args: Omit<UpdateSlackConnectSettings, "id">) => {
		analytics.track("update-slack-settings")
		const result = await zero.mutate(
			mutators.slackConnectSettings.update({ id: settings.id, ...args }),
		).client
		if (result.type === "error") {
			toast.error({
				title: "Failed to update",
				description: "Please try again",
			})
		}
	}

	const notice = needsScopeReconnect(status) ? (
		<div
			className={cn(
				SETTINGS_CARD_NOTICE_CLASS,
				"flex items-center gap-2 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200",
			)}
		>
			<span className="min-w-0 flex-1">{SLACK_MISSING_SCOPES_NOTICE}</span>
			<RoleCheck not={["VIEWER"]}>
				<Button
					variant="outline"
					className="h-7 min-h-7 flex-none"
					onClick={connectSlackWorkspace}
				>
					Reconnect
				</Button>
			</RoleCheck>
		</div>
	) : null

	return (
		<SettingsContainer notice={notice}>
			<div className="flex items-start gap-3">
				<Avatar
					name={name}
					image={status?.slackTeam?.iconUrl ?? undefined}
					roundness="sm"
					size="sm"
					className="flex-none"
				/>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2 text-sm font-medium text-primary">
						<span className="truncate">{name}</span>
						{status?.error && (
							<Badge variant="important">Reconnect needed</Badge>
						)}
						{paused && <Badge variant="amber">Paused</Badge>}
					</div>
					<div className="text-xs text-secondary">{domain}</div>
				</div>
				<RoleCheck not={["VIEWER"]}>
					<DropdownMenu
						trigger={
							<IconButton Icon={Dots} variant="ghost" aria-label="Options" />
						}
						contentProps={{ align: "end" }}
					>
						<DropdownMenuItem onSelect={connectSlackWorkspace}>
							Reconnect
						</DropdownMenuItem>
						<DropdownMenuItem onSelect={() => setDisconnectOpen(true)}>
							Disconnect
						</DropdownMenuItem>
					</DropdownMenu>
				</RoleCheck>
			</div>
			<SettingItem
				className="pt-4"
				title="New channels"
				description="What a channel does when the bot joins."
				action={
					<Select
						name={`new-channels-${settings.id}`}
						value={settings.newChannelsInbox ? "on" : "off"}
						options={NEW_CHANNEL_OPTIONS}
						onChange={(value) =>
							void update({ newChannelsInbox: value === "on" })
						}
						className="w-48"
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title="Message grouping"
				description="How long messages keep joining one thread."
				action={
					<Select
						name={`grouping-${settings.id}`}
						value={groupingValue(settings)}
						options={GROUPING_OPTIONS}
						onChange={(value) => void update(parseGroupingValue(value))}
						className="w-48"
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title="Notifications"
				description="New threads are sent to this channel."
				action={
					<SlackChannelSelect
						name={`notifications-${settings.id}`}
						slackConnectSettingsId={settings.id}
						value={settings.notificationChannelId}
						onChange={(channelId) =>
							update({ notificationChannelId: channelId })
						}
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title="Important notifications"
				description="Important threads are sent to this channel."
				action={
					<SlackChannelSelect
						name={`important-notifications-${settings.id}`}
						slackConnectSettingsId={settings.id}
						value={settings.importantNotificationChannelId}
						// One channel can't take both, or every important thread would post there twice.
						excludeChannelId={settings.notificationChannelId}
						onChange={(channelId) =>
							update({ importantNotificationChannelId: channelId })
						}
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title="Linear Asks"
				description="Links each Ask to its thread. When the issue is done, the thread is too."
				action={
					<Select
						name={`linear-asks-${settings.id}`}
						value={linearAsksModeValue(settings.linearAsksMode)}
						options={[...LINEAR_ASKS_OPTIONS]}
						onChange={(value) =>
							void update({ linearAsksMode: linearAsksModeValue(value) })
						}
						className="w-48"
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title="Thread auto-replies"
				description="Replies in Slack based on your office hours."
				action={
					<Switch
						name={`auto-replies-${settings.id}`}
						checked={settings.pushBotReplyChannel}
						onCheckedChange={(checked) =>
							void update({ pushBotReplyChannel: checked })
						}
					/>
				}
			/>
			<SlackWelcomeMessages
				settings={settings}
				canSendDms={status?.canSendDms}
				slackTeam={status?.slackTeam}
			/>
			<SettingItem
				Icon={Hashtag}
				className="pt-4"
				title="Channels"
				description={channelSummary(
					status?.inboxChannels ?? 0,
					status?.otherChannels ?? 0,
				)}
				link={`/settings/integrations/slack/${settings.id}/channels`}
			/>

			<AlertDialog
				variant="positive"
				isOpen={disconnectOpen}
				onOpenChange={setDisconnectOpen}
				confirmText="Disconnect"
				title={`Disconnect ${name}?`}
				description="Its threads stay in Productlane, and your other Slack workspaces keep syncing."
				onCancel={() => setDisconnectOpen(false)}
				onConfirm={() => {
					analytics.track("disconnect-slack")
					setDisconnectOpen(false)
					disconnect.mutate({ slackConnectSettingsId: settings.id })
				}}
			/>
		</SettingsContainer>
	)
}
